import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminChatConversations, getAdminChatMessages, adminChatReply } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';

export default function AdminChat() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => { if (authLoading) return; if (!user || !isAdmin) { navigate('/admin/login'); return; } loadConvos(); }, [user, isAdmin, navigate, authLoading]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConvos = async () => { try { const res = await getAdminChatConversations(); setConversations(res.data.conversations || []); } catch {} finally { setLoading(false); } };

  const selectUser = async (userId) => {
    setSelectedUser(userId);
    try { const res = await getAdminChatMessages(userId); setMessages(res.data.messages || []); } catch {}
  };

  const handleReply = async () => {
    if (!input.trim() || !selectedUser) return;
    try { await adminChatReply(selectedUser, { message: input.trim() }); setInput(''); const res = await getAdminChatMessages(selectedUser); setMessages(res.data.messages || []); } catch { toast.error(t('toastFailed')); }
  };

  if (authLoading || !user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="mb-8"><h1 className="font-display text-3xl font-bold tracking-tight">{t('liveChat')}</h1><p className="text-muted-foreground mt-1 text-sm">Support conversations</p></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{height: 'calc(100vh - 250px)'}}>
          {/* Conversation list */}
          <Card className="bg-white border-border overflow-hidden">
            <CardContent className="p-0">
              <div className="p-3 border-b border-border"><p className="font-semibold text-sm">Conversations</p></div>
              <div className="overflow-y-auto" style={{maxHeight: '500px'}}>
                {conversations.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">No conversations yet</p>}
                {conversations.map((c) => (
                  <button key={c._id} onClick={() => selectUser(c._id)} className={`w-full text-left p-3 border-b border-border/50 hover:bg-accent/50 transition-colors ${selectedUser === c._id ? 'bg-accent' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm">{c.user_name || 'User'}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.last_msg}</p>
                      </div>
                      {c.unread > 0 && <Badge className="bg-red-500 text-white border-none text-[10px] min-w-[20px] justify-center">{c.unread}</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
          {/* Messages */}
          <Card className="bg-white border-border lg:col-span-2 flex flex-col overflow-hidden">
            <CardContent className="p-0 flex flex-col flex-1">
              {!selectedUser ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground"><MessageCircle className="w-8 h-8 opacity-30 mr-2" /> Select a conversation</div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{maxHeight: '450px'}}>
                    {messages.map((m, i) => (
                      <div key={i} className={`flex ${m.is_admin ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-3 py-2 rounded-xl text-sm ${m.is_admin ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}>
                          {!m.is_admin && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{m.user_name}</p>}
                          {m.message}
                          <p className="text-[9px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={bottomRef} />
                  </div>
                  <div className="p-3 border-t border-border flex gap-2">
                    <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleReply()} placeholder="Type a reply..." className="rounded-xl" />
                    <Button onClick={handleReply} className="bg-primary text-primary-foreground rounded-xl shrink-0 gap-2"><Send className="w-4 h-4" /> Reply</Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
